import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import { ButtonHelper } from '../../Helpers/ButtonHelper.ts';
import ReactDOM from 'react-dom';
import {
  setShowFitAssistant,
  setShowFitGuideModal,
  setShowFitTipsModal,
  setStepEnum,
} from '../../Slices/FitAssistantSlice';
import { IsProductPage } from '../../../FitAssistantShared/IsProductPage.js';
import { getIconClassByFitAssistantStatus } from '../../../FitAssistantShared/Functions.js';
import { onFitAssistantOpen, GAtrackAddToCart } from '../../Analytics/Google.ts';
import {
  CarInformationSelector,
  LastSavedCarInformationSelector,
  CurrentStepSelector,
  FitStatusSelector,
  HasLoadedFromCookieSelector,
  InitialDataSelector,
  ProductDetailsSelector,
  TranslationsSelector,
} from '../../Selectors/FitAssistantSelectors';
import {
  FitTipsSelector,
  MatchingProductsSelector,
  SolutionComponentsSelector,
  SolutionPriceContainerSelector,
  AddToCartButtonSelector,
  ProductPageMatchSelector,
  SelectedSolutionIndexSelector,
} from '../../Selectors/MatchingProductsSelectors';
import FitAssistantStatus from '../../Enums/FitAssistantStatus.ts';
import FitAssistantStep from '../../Enums/FitAssistantStep.ts';
import FitTipsModal from '../Modal/FitTipsModal.tsx';
import FitGuideModal from '../Modal/FitGuideModal.tsx';
import { TrackAddToCart, trackOpen, trackOpenEmpty } from '../../Analytics/Adobe.ts';
import FitAssistantVariation from '../../Enums/FitAssistantVariation.ts';
import Select, { SelectOption } from '/Foundation/Common/code/Views/Foundation/Common/Select.jsx';
import AssistantBanner from '/Foundation/React/code/Components/AssistantBanner/AssistantBanner.tsx';
import { getComponentsForSelectedSolution } from '../../Util/SelectedSolutionComponents.ts';
import { selectSolution } from '../../Util/SelectSolution.ts';

import './FitAssistantButton.less';
import { Constants } from '../../Constants.ts';

const FitAssistantButton = () => {
  const isProductDetailV2 = !!document.querySelector('#product-view.pdp-v2');
  const dispatch = useAppDispatch();
  const carInformation = useAppSelector(CarInformationSelector);
  const lastSavedCarInformation = useAppSelector(LastSavedCarInformationSelector);
  const currentStep = useAppSelector(CurrentStepSelector);
  const fitStatus = useAppSelector(FitStatusSelector);
  const initialData = useAppSelector(InitialDataSelector);
  const translations = useAppSelector(TranslationsSelector);
  const matchingProducts = useAppSelector(MatchingProductsSelector);
  const solutionPriceContainer = useAppSelector(SolutionPriceContainerSelector);
  const addToCartButton = useAppSelector(AddToCartButtonSelector);
  const solutionComponents = useAppSelector(SolutionComponentsSelector);
  const hasLoadedFromCookie = useAppSelector(HasLoadedFromCookieSelector);
  const productPageMatch = useAppSelector(ProductPageMatchSelector);
  const selectedSolution = useAppSelector(SelectedSolutionIndexSelector);
  const productDetail = useAppSelector(ProductDetailsSelector);
  const fitTips = useAppSelector(FitTipsSelector);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isPastLastItem, setIsPastLastItem] = useState(false);
  const portalTarget = document.getElementById('fitAssistantHeaderPortal');
  const productType = window._THULEDATA?.ProductDetail?.ProductType;
  const matchingSolutionsForCurrentProduct =
    matchingProducts?.filter((mp) => mp.PimId === productDetail?.ProductId).length ?? 0;
  const sharedProductIds =
    matchingProducts?.[0]?.ProductIds.filter((id) => matchingProducts.every((mp) => mp.ProductIds.includes(id))) ?? [];

  const rafId = useRef(0);
  const handleScroll = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = 0;
      ButtonHelper.HandleStickyButtonOnScroll(buttonRef, setIsSticky);
      if (!IsProductPage) {
        ButtonHelper.HandleBannerVisibilityOnScroll(setIsPastLastItem);
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    ButtonHelper.HandleStickyButtonOnScroll(buttonRef, setIsSticky);
    if (!IsProductPage) {
      ButtonHelper.HandleBannerVisibilityOnScroll(setIsPastLastItem);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  const handleButtonClick = () => {
    onFitAssistantOpen(carInformation);

    let shortVersions = [
      FitAssistantVariation.CarSeats,
      FitAssistantVariation.DogCrates,
      FitAssistantVariation.TruckBedBikeRacks,
      FitAssistantVariation.TruckAndSpareTireBikeRacks,
    ];

    let hasCarVariation = carInformation.find((x) => x.parameterName === FitAssistantStep.Variation && x.value !== '0');
    let yearFirstHasModel =
      initialData.IsYearFirst &&
      carInformation.find((x) => x.parameterName === FitAssistantStep.Model && x.value !== '0');
    let brandFirstHasYear =
      !initialData.IsYearFirst &&
      carInformation.find((x) => x.parameterName === FitAssistantStep.Year && x.value !== '0');

    let platformsNotFinalized =
      initialData.FitAssistantVariation === FitAssistantVariation.RoofPlatforms &&
      fitStatus !== FitAssistantStatus.RackMounting &&
      !carInformation.find((x) => x.parameterName === FitAssistantStep.RoofRacks && x.value !== '0');

    if (
      ((shortVersions.includes(initialData.FitAssistantVariation) && (yearFirstHasModel || brandFirstHasYear)) ||
        hasCarVariation) &&
      !platformsNotFinalized
    ) {
      trackOpen(initialData.FitAssistantVariation, initialData.IsYearFirst, currentStep, carInformation);
    } else if (fitStatus !== FitAssistantStatus.RackMounting) {
      trackOpenEmpty(initialData.FitAssistantVariation, initialData.IsYearFirst, carInformation, currentStep);
    }

    document.dispatchEvent(new Event('faOpened'));
    dispatch(setShowFitAssistant(true));
    const firstTabable = document.querySelector<HTMLElement>(Constants.WCAG.FIRST_TABABLE);
    firstTabable?.focus();
  };

  const FitInformation = () => {
    return (
      <div className="header-wrapper mb-5">
        <h5 className={`heading5 mb-0`}>
          {translations?.fit}
          {<span className="fit-status paragraph">{ButtonHelper.GetStatusLabel(fitStatus, translations)}</span>}
        </h5>
        {initialData.FitGuide?.LinkText && IsProductPage && (
          <a href="#" onClick={() => dispatch(setShowFitGuideModal(true))}>
            {initialData.FitGuide.LinkText}
          </a>
        )}
      </div>
    );
  };

  const FitAssistantContent = () => {
    return (
      <div className="fit-assistant">
        {IsProductPage && FitInformation()}

        {!IsProductPage ? (
          <AssistantBanner
            icon="le-icon-car"
            text={translations?.findTheRightProductsShort}
            ctaLabel={
              fitStatus === FitAssistantStatus.RackMounting
                ? translations?.rackMounting || ''
                : fitStatus !== FitAssistantStatus.NoSelection
                  ? ButtonHelper.GetCarInformationBtnText(lastSavedCarInformation)
                  : translations?.FitAssistantBannerButtonText || ''
            }
            onCtaClick={handleButtonClick}
            onBannerClick={handleButtonClick}
            className="fit-assistant-banner"
          />
        ) : (
          <button
            className={`fit-assistant-button ${IsProductPage ? 'pdp' : ''}`}
            onClick={handleButtonClick}
            tabIndex={0}
          >
            <div className={`d-flex  ${IsProductPage ? 'align-items-center' : ''}`}>
              <i
                className={`fit-indicator me-5 ${fitStatus} ${IsProductPage ? getIconClassByFitAssistantStatus(fitStatus) : 'le-icon-car'}`}
              ></i>
              {
                <span className={`heading6 ${fitStatus} text-start d-flex align-items-center`}>
                  {ButtonHelper.GetSelectionText(fitStatus, lastSavedCarInformation, translations, IsProductPage)}
                </span>
              }
            </div>
            {!IsProductPage && (
              <div className="icon-wrapper--arrow">
                <i className="le-icon-arrow rot-180"></i>
              </div>
            )}
          </button>
        )}
        {IsProductPage && currentStep === FitAssistantStep.OtherFits && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.otherFitsText}</span>
            <a href={initialData.ParentPageLink} className="text-decoration-underline text-color--gray80">
              {translations.exploreAlternatives}
            </a>
          </div>
        )}
        {IsProductPage && currentStep === FitAssistantStep.NoFits && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.noFitsText}</span>
          </div>
        )}
        {IsProductPage && currentStep === FitAssistantStep.Unconfirmed && (
          <div className="my-3">
            <span className="text-color--gray80">{translations.unconfirmedText}</span>
          </div>
        )}
        {fitTips.length > 0 && IsProductPage && !isProductDetailV2 && (
          <div onClick={() => dispatch(setShowFitTipsModal(true))} className="fit-tip-box p-5 mt-5 d-flex">
            <i className="le-icon-info me-3"></i>
            <div className="d-flex flex-column">
              <span className="mb-1">{translations.vehicleFitTipsTitle}</span>
              <span className="action-text">{translations.learnMore}</span>
            </div>
          </div>
        )}
        {solutionComponents != null &&
          solutionComponents.length > 0 &&
          matchingSolutionsForCurrentProduct > 1 &&
          productPageMatch && (
            <Select
              id="fit-assistant-select"
              label={translations.matchingSolutions}
              options={
                matchingProducts?.map((product, index) => {
                  const rackSolutionFootProduct = product.Products.find((p) => p.Type === 'RackSolution_foot');
                  const rackSolutionFootId = rackSolutionFootProduct?.Id;
                  const matchingComponent = solutionComponents?.find((sc) => sc.ProductId === rackSolutionFootId);
                  const productComponentNames =
                    product.ProductIds.map(
                      (productId) => solutionComponents?.find((sc) => sc.ProductId === productId)?.Name
                    ).filter(Boolean) ?? [];
                  const uniqueComponentNames = product.ProductIds.filter((id) => !sharedProductIds.includes(id))
                    .map((id) => solutionComponents?.find((sc) => sc.ProductId === id))
                    .map((sc) => sc && (sc.Webtitle2 ? `${sc.Name} - ${sc.Webtitle2}` : sc.Name))
                    .filter(Boolean);
                  const name =
                    uniqueComponentNames.length > 0
                      ? uniqueComponentNames.join(', ')
                      : matchingComponent?.Name || `${productComponentNames.join(', ')}`;
                  return {
                    value: index.toString(),
                    name: name,
                  } as SelectOption;
                }) || []
              }
              value={selectedSolution.toString()}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                selectSolution(
                  +e.target.value,
                  { solutionComponents, matchingProducts, solutionPriceContainer, addToCartButton },
                  dispatch
                );
              }}
              onBlur={() => {}}
              additionalClasses="mt-7"
              skipScroll
              placeholder={''}
              hasError={false}
              errorMessage={''}
              isDisabled={false}
            />
          )}
        {IsProductPage && !isProductDetailV2 && solutionComponents && solutionComponents.length > 0 && (
          <div className="my-5 text-color--gray80">
            <span>{translations.includedToFit}</span>
            <ul>
              {getComponentsForSelectedSolution(solutionComponents, matchingProducts, selectedSolution).map(
                (item, index) => (
                  <li key={`${item.ProductId}-${index}`}>
                    {item.Name.includes('Kit')
                      ? item.Name
                      : `${item.Name} ${item.Webtitle2} | ${item.ProductId ?? item.ErpOutdoor}`}
                  </li>
                )
              )}
            </ul>
          </div>
        )}
        {IsProductPage &&
          (initialData.FitAssistantVariation === FitAssistantVariation.CarRoofRacks ||
            (initialData.FitAssistantVariation === FitAssistantVariation.TruckRackSystem &&
              productType?.toLowerCase() === 'dummy product')) &&
          matchingProducts === null &&
          hasLoadedFromCookie && (
            <div className="rrs-button-section">
              <hr className="my-7" />
              <button
                className="rrs-trigger-button btn btn-block thule-btn-shop overflow-hidden"
                onClick={() => {
                  GAtrackAddToCart();
                  TrackAddToCart();
                  dispatch(setStepEnum(FitAssistantStep.VehicleInfoNeeded));
                  dispatch(setShowFitAssistant(true));
                }}
              >
                {translations.addToCart}
              </button>
            </div>
          )}
      </div>
    );
  };

  return (
    <>
      <div ref={buttonRef} />
      {portalTarget && isSticky && !IsProductPage && !isPastLastItem ? (
        <>
          {ReactDOM.createPortal(
            <div className="fit-assistant-isotope-constraint">{FitAssistantContent()}</div>,
            portalTarget
          )}
          <div className="fit-assistant-spacer"></div>
        </>
      ) : !IsProductPage && isPastLastItem ? null : (
        FitAssistantContent()
      )}
      {initialData.FitGuide?.LinkText && IsProductPage && <FitGuideModal />}
      {IsProductPage && (fitTips.length > 0 || isProductDetailV2) && <FitTipsModal />}
    </>
  );
};

export default FitAssistantButton;
